import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Send, CheckCircle2 } from "lucide-react";
import { CARE_TYPES } from "../lib/constants";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LeadForm({ compact = false, title = "Get Started With Care Today" }) {
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    care_type: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [errors, setErrors] = useState({});

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Name is required";
    if (!form.phone.trim() || form.phone.replace(/\D/g, "").length < 7)
      e.phone = "Valid phone number is required";
    if (!form.email.trim() || !/^\S+@\S+\.\S+$/.test(form.email))
      e.email = "Valid email is required";
    if (!form.care_type) e.care_type = "Please choose a care type";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/leads`, form);
      setDone(true);
      toast.success("Request received. A care coordinator will reach out shortly.");
      setForm({ name: "", phone: "", email: "", care_type: "", message: "" });
    } catch (err) {
      const detail = err?.response?.data?.detail || "Something went wrong. Please call us directly.";
      toast.error(typeof detail === "string" ? detail : "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div
        data-testid="lead-form-success"
        className="bg-white rounded-2xl border border-gray-100 shadow-md p-8 text-center"
      >
        <div className="mx-auto w-14 h-14 rounded-full bg-emerald-50 flex items-center justify-center mb-4">
          <CheckCircle2 className="w-7 h-7 text-emerald-600" />
        </div>
        <h3 className="font-heading text-2xl font-semibold text-brand-blue-dark mb-2">
          Thank you for contacting 1st Home Care
        </h3>
        <p className="text-gray-600">
          A care coordinator will reach out shortly. For urgent care,
          please call us at <a className="text-brand-blue font-semibold" href="tel:+17039818201">703-981-8201</a>.
        </p>
        <Button
          onClick={() => setDone(false)}
          data-testid="lead-form-reset"
          variant="outline"
          className="mt-6"
        >
          Submit another request
        </Button>
      </div>
    );
  }

  return (
    <form
      onSubmit={onSubmit}
      data-testid="lead-form"
      className={`bg-white rounded-2xl border border-gray-100 shadow-md p-6 md:p-8 ${
        compact ? "" : "lg:p-10"
      }`}
    >
      <h3 className="font-heading text-2xl md:text-3xl font-semibold text-brand-blue-dark mb-2">
        {title}
      </h3>
      <p className="text-gray-600 mb-6 text-sm">
        Fill out the form and our team will respond within 24 hours. For
        immediate help call <a href="tel:+17039818201" className="text-brand-blue font-semibold">703-981-8201</a>.
      </p>

      <div className="grid gap-4 md:grid-cols-2">
        <div className="md:col-span-1">
          <Label htmlFor="lead-name">Full Name</Label>
          <Input
            id="lead-name"
            data-testid="lead-input-name"
            value={form.name}
            onChange={(e) => update("name", e.target.value)}
            placeholder="Jane Doe"
            className="mt-1"
          />
          {errors.name && <p className="text-xs text-red-600 mt-1">{errors.name}</p>}
        </div>

        <div>
          <Label htmlFor="lead-phone">Phone</Label>
          <Input
            id="lead-phone"
            data-testid="lead-input-phone"
            type="tel"
            value={form.phone}
            onChange={(e) => update("phone", e.target.value)}
            placeholder="(703) 555-0123"
            className="mt-1"
          />
          {errors.phone && <p className="text-xs text-red-600 mt-1">{errors.phone}</p>}
        </div>

        <div className="md:col-span-2">
          <Label htmlFor="lead-email">Email</Label>
          <Input
            id="lead-email"
            data-testid="lead-input-email"
            type="email"
            value={form.email}
            onChange={(e) => update("email", e.target.value)}
            placeholder="you@example.com"
            className="mt-1"
          />
          {errors.email && <p className="text-xs text-red-600 mt-1">{errors.email}</p>}
        </div>

        <div className="md:col-span-2">
          <Label htmlFor="lead-care-type">Type of Care Needed</Label>
          <Select value={form.care_type} onValueChange={(v) => update("care_type", v)}>
            <SelectTrigger id="lead-care-type" data-testid="lead-input-care-type" className="mt-1">
              <SelectValue placeholder="Select a service" />
            </SelectTrigger>
            <SelectContent>
              {CARE_TYPES.map((c) => (
                <SelectItem key={c} value={c} data-testid={`care-option-${c.toLowerCase().replace(/\s+/g, "-")}`}>
                  {c}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {errors.care_type && <p className="text-xs text-red-600 mt-1">{errors.care_type}</p>}
        </div>

        <div className="md:col-span-2">
          <Label htmlFor="lead-message">Message (optional)</Label>
          <Textarea
            id="lead-message"
            data-testid="lead-input-message"
            value={form.message}
            onChange={(e) => update("message", e.target.value)}
            placeholder="Tell us about your situation, schedule, or concerns…"
            rows={4}
            className="mt-1"
          />
        </div>
      </div>

      <Button
        type="submit"
        data-testid="lead-form-submit"
        disabled={submitting}
        className="w-full mt-6 h-12 bg-brand-blue hover:bg-brand-blue-dark text-white font-semibold rounded-full shadow-md hover:shadow-lg transition-all"
      >
        {submitting ? (
          <>
            <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Submitting…
          </>
        ) : (
          <>
            <Send className="w-4 h-4 mr-2" /> Submit Request
          </>
        )}
      </Button>

      <p className="mt-3 text-xs text-gray-500 text-center">
        We respect your privacy. Your information is sent securely to our care team.
      </p>
    </form>
  );
}
